import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import ResumeBuilder from "@/components/resume/ResumeBuilder";
import CVOverviewList from "@/components/CVOverviewList";
import ShareCvCard from "@/components/ShareCvCard";
import PendingJobHint from "@/components/PendingJobHint";
import ThinkingLoader from "@/components/ThinkingLoader";
import VideoLetterModal from "@/components/VideoLetterModal";
import { FileText, UploadCloud, Trash2, RefreshCw, PenTool, Video, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

function formatBytes(b) {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

function CVUploadCard() {
  const { t } = useT();
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cv");
      setCv(data.present ? data : null);
    } catch {
      setCv(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/cv/upload", fd);
      toast.success(t("cvUploaded"));
      setCv({ ...data, present: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("cvUploadFailed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    if (!(await confirm(t("cvConfirmDelete")))) return;
    try {
      await api.delete("/cv");
      setCv(null);
      toast.success(t("cvDeleted"));
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div>
      <p className="font-serif text-2xl italic text-neutral-800 mb-6">{t("cvSubtitle")}</p>
      <div className="ed-card p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {loading ? (
          <p className="ed-eyebrow">{t("loading")}</p>
        ) : cv ? (
          <div className="flex-1 min-w-0">
            <p className="font-serif text-xl text-neutral-900 truncate">{cv.filename}</p>
            <p className="font-mono text-xs text-neutral-500 mt-1">
              {formatBytes(cv.size)} · {t("cvUpdatedAt")}: {new Date(cv.updated_at).toLocaleString()}
            </p>
          </div>
        ) : (
          <p className="text-sm text-neutral-500 italic font-serif">{t("cvNone")}</p>
        )}

        <div className="flex gap-2 shrink-0">
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            data-testid="cv-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            data-testid="cv-upload-button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="ed-btn"
          >
            {cv ? <RefreshCw size={14} strokeWidth={1.5} /> : <UploadCloud size={14} strokeWidth={1.5} />}
            {uploading ? t("cvUploading") : (cv ? t("cvReplace") : t("cvUpload"))}
          </button>
          {uploading && (
            <ThinkingLoader stage="resume" testId="cv-upload-thinking" />
          )}
          {cv && (
            <button
              type="button"
              data-testid="cv-delete-button"
              onClick={handleDelete}
              className="ed-btn ed-btn-outline"
              title={t("cvRemove")}
            >
              <Trash2 size={14} strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-neutral-500 font-mono mt-3">{t("cvHint")}</p>
    </div>
  );
}

const TABS = [
  { key: "overview", labelKey: "cvTabOverview", Icon: FileText },
  { key: "builder", labelKey: "cvTabBuilder", Icon: PenTool },
];


function ElevatorPitchCard() {
  const { t } = useT();
  const [pitch, setPitch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [labelDraft, setLabelDraft] = useState("");
  const [labelSaving, setLabelSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/video-letters/elevator-pitch");
      setPitch(r.data || null);
      setLabelDraft((r.data && r.data.qr_label) || "");
    } catch {
      setPitch(null);
      setLabelDraft("");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const saveLabel = async () => {
    if (!pitch) return;
    setLabelSaving(true);
    try {
      await api.patch(`/video-letters/${pitch.id}/qr-label`, { qr_label: labelDraft });
      toast.success(t("vidQrLabelSaved"));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("vidQrLabelFail"));
    } finally {
      setLabelSaving(false);
    }
  };

  const handleClose = () => {
    setModalOpen(false);
    // Reload state so a freshly-recorded pitch surfaces its view counter
    // and preview card without a manual refresh.
    refresh();
  };

  return (
    <div className="ed-card p-6" data-testid="elevator-pitch-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-[16rem]">
          <div className="w-9 h-9 rounded-full bg-amber-500 text-neutral-900 flex items-center justify-center shrink-0">
            <Video size={15} strokeWidth={1.7} />
          </div>
          <div>
            <p className="font-serif text-base leading-tight">{t("vidElevatorCardTitle")}</p>
            <p className="text-xs text-neutral-600 mt-1 leading-relaxed max-w-lg">{t("vidElevatorCardBody")}</p>
            {!loading && pitch && (
              <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="elevator-pitch-active">
                <a
                  href={`/video/${pitch.public_token}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 font-mono text-[0.65rem] uppercase tracking-[0.12em] text-amber-800 hover:text-amber-900 underline-offset-2 hover:underline"
                  data-testid="elevator-pitch-open"
                >
                  <PlayCircle size={11} strokeWidth={1.7} /> {t("vidExistingOpen")}
                </a>
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.12em] text-neutral-500" data-testid="elevator-pitch-views">
                  {t("vidElevatorHasOne", { views: pitch.view_count || 0 })}
                </span>
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          data-testid="elevator-pitch-record"
          onClick={() => setModalOpen(true)}
          className="ed-btn inline-flex items-center gap-2"
        >
          <Video size={14} strokeWidth={1.6} />
          {pitch ? t("vidElevatorReplace") : t("vidElevatorCta")}
        </button>
      </div>

      {!loading && pitch && (
        <div className="mt-4 pt-4 border-t border-neutral-200 space-y-2" data-testid="elevator-pitch-qr-label">
          <p className="ed-label">{t("vidQrLabelTitle")}</p>
          <p className="text-xs text-neutral-500">{t("vidQrLabelHint")}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              data-testid="elevator-pitch-qr-label-input"
              className="ed-input flex-1 min-w-[16rem] font-mono text-xs"
              placeholder={t("vidQrLabelPlaceholder")}
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value.slice(0, 60))}
              maxLength={60}
            />
            <button
              type="button"
              data-testid="elevator-pitch-qr-label-save"
              onClick={saveLabel}
              disabled={labelSaving || (labelDraft || "") === (pitch.qr_label || "")}
              className="ed-btn ed-btn-outline"
            >
              {labelSaving ? t("vidQrLabelSaving") : t("vidQrLabelSave")}
            </button>
          </div>
        </div>
      )}

      {modalOpen && (
        <VideoLetterModal elevator letter={null} onClose={handleClose} />
      )}
    </div>
  );
}


export default function CVPanel({ onLaunchJobWizard }) {
  const confirm = useConfirm();
  const { t } = useT();
  const [tab, setTab] = useState("overview");
  const [openVariantId, setOpenVariantId] = useState(null);

  const handleOpen = (rid) => {
    setOpenVariantId(rid);
    setTab("builder");
  };

  return (
    <section className="mb-14" data-testid="cv-panel">
      <div className="mb-5">
        <p className="ed-eyebrow flex items-center gap-2">
          <FileText size={12} strokeWidth={1.5} /> {t("cvTitle")}
        </p>
      </div>

      <nav className="flex border-b border-neutral-300 mb-8" data-testid="cv-tabs">
        {TABS.map(({ key, labelKey, Icon }) => (
          <button
            key={key}
            data-testid={`cv-tab-${key}`}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-[0.15em] border-b-2 -mb-px whitespace-nowrap transition-colors ${
              tab === key
                ? "border-neutral-900 text-neutral-900"
                : "border-transparent text-neutral-500 hover:text-neutral-900"
            }`}
          >
            <Icon size={13} strokeWidth={1.5} />
            {t(labelKey)}
          </button>
        ))}
      </nav>

      {tab === "overview" && (
        <div className="space-y-8">
          <PendingJobHint
            onOpenBuilder={() => setTab("builder")}
            onGenerated={(resume) => { setOpenVariantId(resume.id); setTab("builder"); }}
          />
          <CVUploadCard />
          <ElevatorPitchCard />
          <div>
            <p className="ed-label mb-3">{t("cvOvVariantsTitle")}</p>
            <div className="border-l-4 border-rose-400 bg-rose-50 text-neutral-800 px-4 py-3 mb-4 text-sm leading-relaxed" data-testid="cv-default-note">
              {t("cvDefaultNote")}
            </div>
            <CVOverviewList onOpenVariant={handleOpen} />
          </div>
        </div>
      )}
      {tab === "builder" && (
        <ResumeBuilder
          onLaunchJobWizard={onLaunchJobWizard}
          initialResumeId={openVariantId}
          onSelected={() => setOpenVariantId(null)}
        />
      )}
      <ShareCvCard />
    </section>
  );
}
